import { DOCUMENT } from '@angular/common';
import {
  ChangeDetectionStrategy,
  ChangeDetectorRef,
  Component,
  ElementRef,
  EventEmitter,
  Inject,
  Input,
  OnChanges,
  OnDestroy,
  OnInit,
  Output,
  SimpleChange,
} from '@angular/core';
import { AbstractControl, FormGroup } from '@angular/forms';
import { fromJS, List, Map } from 'immutable';
import { isUndefined } from 'lodash';
import { Subject } from 'rxjs';
import { DiscountTypesEnum } from '../../../document-data/sub-models/discount.model';
import {
  DocumentItem,
  MarkupTypesEnum,
} from '../../../document-data/sub-models/document-item.model';
import { ISelectOption } from '../../../form-master/components/select-field/select-field.interface';
import { IValueSwitch } from '../../../form-master/components/value-switch-field/value-switch-field.component';
import { ItemUnitTypesEnum } from '../../../item-data/item-data.data';
import { Tax } from '../../../settings-data/sub-models/company-settings/tax.model';
import { WithholdingTax } from '../../../settings-data/sub-models/company-settings/withholding-tax.model';
import { DocumentPresetSettings } from '../../../settings-data/sub-models/document-preset-settings/document-preset-settings.model';
import { IIcon } from '../../../shared/components/icon/icon.interface';
import { IconSizesEnum } from '../../../shared/components/icon/icons-sizes.enum';
import { LoggerService } from '../../../shared/services/logger/logger.service';
import { I2gNgTrackingActionBuilder } from 'i2g-ng-tracking';
import { TrackingObjectBuilder } from '../../../tracking/tracking-object.builder';
import {
  ITrackingAction,
  ITrackingObject,
  TrackingDialogNamesEnum,
  TrackingScreenNamesEnum,
} from '../../../tracking/tracking.data';
import { DocumentItemOptionsBuilder } from './document-item-options-builder';
import { TrackingDispatcher } from '../../../tracking/tracking.dispatcher';

export interface IDocumentItemOptionsInfo {
  documentItemProps: Map<string, any>;
  markup: IMarkupInfo;
}

export interface IMarkupInfo {
  type: MarkupTypesEnum;
  value: number;
}

@Component({
  selector: 'app-document-item-options',
  templateUrl: './document-item-options.component.html',
  styleUrls: ['./document-item-options.component.scss'],
  changeDetection: ChangeDetectionStrategy.OnPush,
  viewProviders: [DocumentItemOptionsBuilder],
})
export class DocumentItemOptionsComponent
  implements OnInit, OnChanges, OnDestroy {
  @Input() taxes: List<Tax>;
  @Input() withholdingTax: WithholdingTax;
  @Input() documentItem: DocumentItem;
  @Input() currencyCode: string;
  @Input() locale: string;
  @Input() showDiscount: boolean;
  @Input() showMarkup: boolean;
  @Input() documentSettings: DocumentPresetSettings;
  @Input() trackingObject: ITrackingObject;
  @Input() trackingScreenName: TrackingScreenNamesEnum;

  @Output()
  onUpdateDocumentItemOptions: EventEmitter<IDocumentItemOptionsInfo> = new EventEmitter<IDocumentItemOptionsInfo>();

  private _el: any;
  private _destroyed: Subject<boolean> = new Subject<boolean>();
  private _handleClickOutsideCallback = this._handleClickOutside.bind(this);

  showPopup: boolean;
  form: FormGroup;
  formKeys: any;
  DiscountTypesEnum: typeof DiscountTypesEnum;
  discountSwitches: List<IValueSwitch>;
  taxRates: { [taxId: string]: List<ISelectOption> };
  moreOptionsAction: ITrackingAction;
  taxDropdownSelectedAction: ITrackingAction;
  markupSwitches: List<IValueSwitch>;
  MarkupTypesEnum: typeof MarkupTypesEnum;
  triggerText: string;
  partsOrLaborOptions: List<ISelectOption>;
  laborTypeOptions: List<ISelectOption>;
  chevronDownIcon: IIcon;
  partsOrLaborSelected: ITrackingAction;
  laborTypeSelected: ITrackingAction;

  constructor(
    private _elementRef: ElementRef,
    @Inject(DOCUMENT) private _document: Document,
    private _changeDetectorRef: ChangeDetectorRef,
    private _documentItemOptionsBuilder: DocumentItemOptionsBuilder,
    private _loggerService: LoggerService,
    private _trackingDispatcher: TrackingDispatcher,
    private _trackingObjectBuilder: TrackingObjectBuilder,
    private _trackingActionBuilder: I2gNgTrackingActionBuilder
  ) {}

  ngOnChanges(changes) {
    const taxesChanges: SimpleChange = changes.taxes;

    if (taxesChanges) {
      this.taxRates = this._documentItemOptionsBuilder.buildTaxRates(
        this.taxes,
        this.locale
      );
      this.triggerText = this._documentItemOptionsBuilder.buildTriggerTextKey(
        this.taxes,
        this.showDiscount,
        this.documentSettings
      );
    }

    if (
      (taxesChanges || changes.documentItem || changes.documentSettings) &&
      this.documentItem &&
      this.taxes &&
      this.withholdingTax &&
      this.documentSettings
    ) {
      if (!this.form) {
        this.formKeys = this._documentItemOptionsBuilder.FORM_KEYS;
        this.form = this._documentItemOptionsBuilder.buildForm(
          this.documentItem,
          this.taxes,
          this.withholdingTax,
          this.documentSettings.separatePartsAndLabor
        );
      }
    }
  }

  ngOnInit() {
    this._el = this._elementRef.nativeElement;

    this.moreOptionsAction = this._trackingActionBuilder.buildButtonTapped(
      'more_options',
      this.trackingScreenName
    );
    this.taxDropdownSelectedAction = this._trackingActionBuilder.buildDropdownSelected(
      'tax',
      this.trackingScreenName
    );
    this.MarkupTypesEnum = MarkupTypesEnum;
    this.DiscountTypesEnum = DiscountTypesEnum;
    this.chevronDownIcon = {
      name: 'chevron-down',
      size: IconSizesEnum.small,
    };
    this.discountSwitches = this._documentItemOptionsBuilder.buildDiscountSwitches(
      this.currencyCode,
      this.trackingObject,
      this.trackingScreenName
    );
    this.markupSwitches = this._documentItemOptionsBuilder.buildMarkupSwitches(
      this.currencyCode,
      this.trackingObject,
      this.trackingScreenName
    );
    this.partsOrLaborOptions = this._documentItemOptionsBuilder.buildPartsOrLaborOptions();
    this.laborTypeOptions = this._documentItemOptionsBuilder.buildLaborTypeOptions(
      this.documentSettings.separatePartsAndLabor ||
        this.documentItem.isTrackedTimeReference()
    );
    this.partsOrLaborSelected = this._trackingActionBuilder.buildDropdownSelected(
      'parts_labor',
      this.trackingScreenName
    );
    this.laborTypeSelected = this._trackingActionBuilder.buildDropdownSelected(
      'days_hours',
      this.trackingScreenName
    );
  }

  getFormKeysForTax(tax: Tax): string[] {
    return this._documentItemOptionsBuilder.buildFormKeysForTax(tax.id);
  }

  togglePopup(flag?: boolean) {
    this.showPopup = isUndefined(flag) ? !this.showPopup : flag;

    if (this.showPopup) {
      this._trackingDispatcher.trackEvent(
        this._trackingObjectBuilder.buildDialog(
          TrackingDialogNamesEnum.document_item_options
        ),
        this._trackingActionBuilder.buildPresented(this.trackingScreenName)
      );

      setTimeout(() =>
        this._document.addEventListener(
          'click',
          this._handleClickOutsideCallback
        )
      );
    } else {
      this._document.removeEventListener(
        'click',
        this._handleClickOutsideCallback
      );
    }
  }

  updateDocumentItemOptions(documentItemOptionsInfo: Map<string, any>) {
    const form = this.form;
    const formKeys = this.formKeys;
    const separatePartsAndLabor: boolean = this.documentSettings
      .separatePartsAndLabor;
    const discountAmountControl: AbstractControl = form.get(
      formKeys.DISCOUNT_AMOUNT
    );
    const discountPercentageControl: AbstractControl = form.get(
      formKeys.DISCOUNT_PERCENTAGE
    );
    const discountTypeControl: AbstractControl = form.get(
      formKeys.DISCOUNT_TYPE
    );
    const markupAmountControl: AbstractControl = form.get(
      formKeys.MARKUP_AMOUNT
    );
    const markupPercentageControl: AbstractControl = form.get(
      formKeys.MARKUP_PERCENTAGE
    );
    const markupTypeControl: AbstractControl = form.get(formKeys.MARKUP_TYPE);
    const partsOrLaborControl: AbstractControl = form.get(
      formKeys.PARTS_OR_LABOR
    );
    const enableLaborTypeControl: AbstractControl = form.get(
      formKeys.ENABLE_LABOR_TYPE
    );
    const laborTypeControl: AbstractControl = form.get(formKeys.LABOR_TYPE);
    const markupType: MarkupTypesEnum = markupTypeControl.value;
    const markupControl: AbstractControl =
      markupType === MarkupTypesEnum.amount
        ? markupAmountControl
        : markupPercentageControl;
    const noEmitEvent = { onlySelf: true, emitEvent: false };

    this.taxes.forEach((tax: Tax) => {
      const [
        enableTaxKey,
        taxRateKey,
      ] = this._documentItemOptionsBuilder.buildFormKeysForTax(tax.id);
      const taxRateControl = form.get(taxRateKey);

      if (taxRateControl) {
        if (documentItemOptionsInfo.get(enableTaxKey, false)) {
          taxRateControl.enable(noEmitEvent);
        } else {
          taxRateControl.disable(noEmitEvent);
        }
      }
    });

    if (separatePartsAndLabor) {
      if (partsOrLaborControl.value === ItemUnitTypesEnum.parts) {
        enableLaborTypeControl.disable(noEmitEvent);
        laborTypeControl.disable(noEmitEvent);
      } else {
        enableLaborTypeControl.enable(noEmitEvent);
      }
    }

    if (enableLaborTypeControl.enabled && enableLaborTypeControl.value) {
      laborTypeControl.enable(noEmitEvent);
    } else {
      laborTypeControl.disable(noEmitEvent);
    }

    if (documentItemOptionsInfo.get(formKeys.ENABLE_DISCOUNT, false)) {
      discountAmountControl.enable(noEmitEvent);
      discountPercentageControl.enable(noEmitEvent);
      discountTypeControl.enable(noEmitEvent);
    } else {
      discountAmountControl.disable(noEmitEvent);
      discountPercentageControl.disable(noEmitEvent);
      discountTypeControl.disable(noEmitEvent);
    }

    if (documentItemOptionsInfo.get(formKeys.ENABLE_MARKUP, false)) {
      markupAmountControl.enable(noEmitEvent);
      markupPercentageControl.enable(noEmitEvent);
      markupTypeControl.enable(noEmitEvent);
    } else {
      markupAmountControl.disable(noEmitEvent);
      markupPercentageControl.disable(noEmitEvent);
      markupTypeControl.disable(noEmitEvent);
    }

    form.updateValueAndValidity(noEmitEvent);

    this._loggerService.log('updating tax discount info:', form.value);

    this.onUpdateDocumentItemOptions.emit({
      documentItemProps: this._documentItemOptionsBuilder.buildDocumentItemProps(
        fromJS(form.value),
        this.taxes,
        separatePartsAndLabor
      ),
      markup: !isUndefined(markupType)
        ? {
            type: markupType,
            value: markupControl.value || 0,
          }
        : null,
    });
  }

  ngOnDestroy() {
    this._document.removeEventListener(
      'click',
      this._handleClickOutsideCallback
    );

    this._destroyed.next(true);
    this._destroyed.unsubscribe();
  }

  private _handleClickOutside(evt: Event) {
    const evtTarget = evt.target;

    if (!evtTarget) {
      return;
    }

    if (!this._el.contains(evtTarget)) {
      this.togglePopup(false);
      this._changeDetectorRef.markForCheck();
    }
  }
}
